/**
 * templateLayoutEdits.ts
 * Persists layout / format edits made on the uploaded template's rendered preview
 * (text changes, drag repositioning, format overrides) so they are re-applied to the
 * final generated documents (PDF / print).
 *
 * Matching strategy
 * ----------------
 * docx-preview renders the marker template and the filled document with the SAME DOM
 * structure (sections, articles, paragraphs, tables). Each edited element is keyed by
 * its **structural path** — the chain of child indices from the <section> root down to
 * the element (e.g. "ARTICLE:0/P:1/SPAN:0"). This path is identical in the template and
 * the filled render, so edits re-apply reliably even though the element's text changed.
 * Repeat tables add extra <tr> rows but leave the surrounding structure unchanged, so
 * paragraph/table/section-level edits still match; cell edits inside a repeat region
 * apply to the first row that shares the same path.
 */

import type { RenderedPage } from "@/types/print";

const MARKER_RE = /{{\#?[^{}]*}}|⟦[^⟧]*⟧/g;
const EDITED_TEXT_ATTR = "data-user-edited-text";
const FORMAT_ATTR = "data-format-edited";
const DELETED_ATTR = "data-user-deleted";

const EDITABLE_SELECTOR = "p, td, th, span, div, h1, h2, h3, h4, table";

/** Inline style keys captured/restored as "format" edits. */
const FORMAT_KEYS = [
  "font-size",
  "font-weight",
  "font-style",
  "text-align",
  "color",
  "background-color",
  "text-decoration",
  "line-height",
  "border-width",
  "border-style",
  "border-color",
  // Horizontal alignment of a whole block within the page.
  "margin-left",
  "margin-right",
  "display",
  "width",
];

export interface TemplateLayoutEdit {
  kind: "label" | "table";
  /** The structural path anchored to the <section> root. */
  key: string;
  /** The edited text (for text edits). */
  text?: string;
  transform?: { x: number; y: number };
  format?: Record<string, string>;
  /** Indicates that the element was deleted / hidden from the layout. */
  hidden?: boolean;
}

function getSections(container: HTMLElement): HTMLElement[] {
  if (!container) return [];
  if (container.classList && container.classList.contains("docx")) return [container];
  return Array.from(container.querySelectorAll<HTMLElement>("section.docx"));
}

/**
 * Compute the structural path (child-index chain) from the section root to `el`.
 * Only element tags are used so the path is stable between template and filled renders.
 */
function pathKey(section: HTMLElement, el: HTMLElement): string {
  const parts: string[] = [];
  let cur: HTMLElement | null = el;
  while (cur && cur !== section) {
    const parent: HTMLElement | null = cur.parentElement;
    if (!parent) break;
    const idx = Array.from(parent.children).indexOf(cur);
    parts.unshift(`${cur.tagName}:${idx}`);
    cur = parent;
  }
  return parts.join("/");
}

function getTransform(el: HTMLElement): { x: number; y: number } | undefined {
  const style = window.getComputedStyle(el);
  const t = style.transform || el.style.transform || "";
  const m = /translate\(\s*(-?[0-9.]+)px\s*,\s*(-?[0-9.]+)px\s*\)/.exec(t);
  if (m) {
    const x = parseFloat(m[1]);
    const y = parseFloat(m[2]);
    if (x !== 0 || y !== 0) return { x, y };
  }
  return undefined;
}

function captureFormat(el: HTMLElement): Record<string, string> | undefined {
  if (!el.hasAttribute(FORMAT_ATTR)) return undefined;
  const out: Record<string, string> = {};
  for (const k of FORMAT_KEYS) {
    const v = el.style.getPropertyValue(k);
    if (v) out[k] = v;
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * Walk the rendered template preview and serialize every user edit (text, drag,
 * format, deletion) into a list of TemplateLayoutEdit records keyed by structural path.
 */
export function captureTemplateLayoutEdits(container: HTMLElement): TemplateLayoutEdit[] {
  const edits: TemplateLayoutEdit[] = [];
  const sections = getSections(container);

  sections.forEach((sec) => {
    const els = Array.from(sec.querySelectorAll<HTMLElement>(EDITABLE_SELECTOR));

    els.forEach((el) => {
      const isEditedText = el.hasAttribute(EDITED_TEXT_ATTR);
      const transform = getTransform(el);
      const format = captureFormat(el);
      const isDeleted = el.hasAttribute(DELETED_ATTR) || el.style.display === "none";
      if (!isEditedText && !transform && !format && !isDeleted) return;

      const kind: "label" | "table" = el.tagName === "TABLE" ? "table" : "label";
      const edit: TemplateLayoutEdit = { kind, key: pathKey(sec, el) };
      if (isEditedText) edit.text = (el.textContent || "").replace(MARKER_RE, "");
      if (transform) edit.transform = transform;
      if (format) edit.format = format;
      if (isDeleted) edit.hidden = true;
      edits.push(edit);
    });
  });

  return edits;
}

function applyStyleEdits(el: HTMLElement, edit: TemplateLayoutEdit): void {
  if (edit.hidden) {
    el.style.display = "none";
    el.setAttribute(DELETED_ATTR, "true");
  } else if (edit.hidden === false) {
    el.style.display = "";
    el.removeAttribute(DELETED_ATTR);
  }
  if (edit.transform) {
    el.style.position = el.style.position || "relative";
    el.style.transform = `translate(${edit.transform.x}px, ${edit.transform.y}px)`;
  }
  if (edit.format) {
    for (const [k, v] of Object.entries(edit.format)) {
      el.style.setProperty(k, v);
    }
  }
}

function applyTextEdit(el: HTMLElement, text: string): void {
  // If the element itself is a leaf (span), set it directly; otherwise set the first
  // leaf span so the surrounding data is preserved.
  if (el.children.length === 0) {
    el.textContent = text;
    return;
  }
  const leaf = Array.from(el.querySelectorAll("span")).find((sp) => sp.children.length === 0);
  if (leaf) leaf.textContent = text;
}

/** Apply a list of edits onto a single rendered <section> element. */
export function applyTemplateLayoutEdits(
  section: HTMLElement,
  edits: TemplateLayoutEdit[]
): void {
  if (!section || !edits || edits.length === 0) return;
  const els = Array.from(section.querySelectorAll<HTMLElement>(EDITABLE_SELECTOR));
  const byPath = new Map<string, HTMLElement>();
  els.forEach((el) => {
    const k = pathKey(section, el);
    if (!byPath.has(k)) byPath.set(k, el);
  });

  for (const edit of edits) {
    const target = byPath.get(edit.key);
    if (!target) continue;
    applyStyleEdits(target, edit);
    if (edit.text !== undefined) applyTextEdit(target, edit.text);
    // A "deleted" table cell should hide the whole column across every row —
    // the structural path only matches the first row of a repeated table, so
    // without this the column reappears on every repeated data row.
    if (edit.hidden && (target.tagName === "TD" || target.tagName === "TH")) {
      hideCellColumn(target);
    }
  }
}

/** Hide the same column index in every row of the table `cell` belongs to. */
function hideCellColumn(cell: HTMLElement): void {
  const row = cell.parentElement as HTMLTableRowElement | null;
  const table = cell.closest("table");
  if (!row || !table) return;
  const colIndex = Array.from(row.children).indexOf(cell);
  if (colIndex < 0) return;
  table.querySelectorAll("tr").forEach((tr) => {
    const sibling = tr.children[colIndex] as HTMLElement | undefined;
    if (sibling && sibling !== cell) {
      sibling.style.display = "none";
      sibling.setAttribute(DELETED_ATTR, "true");
    }
  });
}

/** Apply layout edits directly to DOM nodes already rendered in a container. */
export function applyLayoutEditsToContainer(
  container: HTMLElement,
  edits: TemplateLayoutEdit[]
): void {
  if (!container) return;
  getSections(container).forEach((sec) => {
    applyTemplateLayoutEdits(sec, edits);
  });
}

/** Re-apply edits onto RenderedPage fragments before they are exported to PDF / print. */
export function applyLayoutEditsToPages(
  pages: RenderedPage[],
  edits: TemplateLayoutEdit[]
): RenderedPage[] {
  if (!edits || edits.length === 0) return pages;
  return pages.map((p) => {
    const holder = document.createElement("div");
    holder.innerHTML = p.html;
    const section = holder.querySelector<HTMLElement>("section.docx");
    if (section) {
      applyTemplateLayoutEdits(section, edits);
      return { ...p, html: section.outerHTML };
    }
    return p;
  });
}